/**
 * Refused rows, fixed in place and sent again as a correction source.
 *
 * A receipt written since row data was kept records the row's `data` exactly as
 * it was sent, so a refused row can be rebuilt, fixed field by field and sent
 * again. A fixed row alone is not enough: the server resolves a row's
 * references only to rows in the same source, so a fixed section needs its term
 * and instructors with it. The parents are rebuilt from this run's own receipts
 * by the dependency walk the server's retry scope uses (`_retry_scope` in
 * `app/services/imports.py`). A parent this run already created or matched is
 * sent so that it matches by its key and never creates again.
 *
 * The typed fields mirror the server's row contracts (`TermData`,
 * `CourseData`, `StudentData`, `EnrollmentData`, `OutcomeData`,
 * `RegistrarStudentData`, `RegistrarEnrollmentData`) and its correction
 * contracts (`TermCorrection` ... `OutcomeCorrection`). The server validates
 * every row again on the dry run; these specs only choose the control.
 */
import {
  RECEIPT_SERVICE_KEYS, receiptLearnerChoice, receiptRow,
  type EntityKind, type ImportKind, type ImportReceipt, type ImportRowInput, type LearnerChoice,
} from "@/lib/api/imports";

export type FieldType = "text" | "date" | "integer" | "decimal" | "keys" | "choice";
export interface RowField { name: string; type: FieldType; required?: true; options?: readonly string[] }

const STUDENT_STATUS = ["completed", "active", "leave_of_absence", "withdrawn"] as const;
const ENROLLMENT_STATUS = ["complete", "enrolled", "withdrawn", "incomplete"] as const;

/** The fields a refused row is fixed in, by source contract and kind. Instructor rows are a record choice instead. */
export const ROW_FIELDS: Record<ImportKind, Partial<Record<EntityKind, readonly RowField[]>>> = {
  historical: {
    term: [{ name: "name", type: "text", required: true }, { name: "start_date", type: "date" }, { name: "end_date", type: "date" }, { name: "grades_due_date", type: "date" }],
    course: [{ name: "term_key", type: "text", required: true }, { name: "code", type: "text", required: true }, { name: "title", type: "text", required: true },
      { name: "credits", type: "integer", required: true }, { name: "primary_faculty_key", type: "text" }, { name: "co_instructor_faculty_keys", type: "keys" },
      { name: "status", type: "choice", options: ["archived", "draft"] }],
    student: [{ name: "student_id", type: "text" }, { name: "legal_name", type: "text", required: true }, { name: "email", type: "text" },
      { name: "status", type: "choice", options: STUDENT_STATUS }],
    enrollment: [{ name: "student_key", type: "text", required: true }, { name: "course_key", type: "text", required: true },
      { name: "status", type: "choice", options: ENROLLMENT_STATUS }],
    outcome: [{ name: "enrollment_key", type: "text", required: true }, { name: "result_letter", type: "text", required: true }, { name: "result_score", type: "decimal" }],
  },
  registrar_roster: {
    student: [{ name: "student_id", type: "text" }, { name: "given_name", type: "text", required: true }, { name: "family_name", type: "text", required: true },
      { name: "delivery_email", type: "text" }, { name: "program_key", type: "text" }, { name: "cohort_key", type: "text" }, { name: "admitted_year", type: "integer" }],
    enrollment: [{ name: "student_key", type: "text", required: true }, { name: "course_code", type: "text", required: true }, { name: "term_name", type: "text", required: true }],
  },
};

/** The fields an applied record's correction names; a blank field is left as it stands. */
export const CORRECTION_FIELDS: Record<EntityKind, readonly RowField[]> = {
  term: [{ name: "name", type: "text" }, { name: "start_date", type: "date" }, { name: "end_date", type: "date" }, { name: "grades_due_date", type: "date" },
    { name: "status", type: "choice", options: ["closed", "active", "upcoming"] }],
  course: [{ name: "code", type: "text" }, { name: "title", type: "text" }, { name: "credits", type: "integer" }, { name: "status", type: "choice", options: ["archived", "draft"] }],
  faculty: [{ name: "display_name", type: "text", required: true }],
  student: [{ name: "legal_name", type: "text" }, { name: "email", type: "text" }, { name: "status", type: "choice", options: STUDENT_STATUS }],
  enrollment: [{ name: "status", type: "choice", options: ENROLLMENT_STATUS }],
  outcome: [{ name: "result_letter", type: "text", required: true }, { name: "result_score", type: "decimal" }],
};

/** A recorded value as a field shows it. */
export function shownValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.every((v) => typeof v === "string")) return value.join("; ");
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/** A field's entry as the server reads it; undefined leaves the field out. */
export function fieldValue(field: RowField, raw: string): unknown {
  const value = raw.trim();
  if (!value) return undefined;
  if (field.type === "keys") {
    const keys = value.split(";").map((v) => v.trim()).filter(Boolean);
    return keys.length ? keys : undefined;
  }
  if (field.type === "integer" && /^-?\d+$/.test(value)) return Number(value);
  if (field.type === "decimal" && /^\d+(\.\d+)?$/.test(value)) return Number(value);
  return value;
}

/** Fields whose entry cannot be sent: a required field left blank, or a number that is not one. */
export interface FieldProblem { field: string; problem: "required" | "number" }
export function fieldProblems(fields: readonly RowField[], values: Record<string, string>): FieldProblem[] {
  return fields.flatMap((f): FieldProblem[] => {
    const value = (values[f.name] ?? "").trim();
    if (!value) return f.required ? [{ field: f.name, problem: "required" as const }] : [];
    if (f.type === "integer" && !/^-?\d+$/.test(value)) return [{ field: f.name, problem: "number" as const }];
    if (f.type === "decimal" && !/^\d+(\.\d+)?$/.test(value)) return [{ field: f.name, problem: "number" as const }];
    return [];
  });
}

/** Recorded fields the contract does not name, such as a text-only instructor name: shown, and removable. */
export function otherFields(fields: readonly RowField[], data: Record<string, unknown>): string[] {
  return Object.keys(data).filter((key) => !fields.some((f) => f.name === key));
}

/** A row's fields as its recorded data fills them. */
export function valuesOf(fields: readonly RowField[], data: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries([...fields.map((f) => f.name), ...otherFields(fields, data)].map((name) => [name, shownValue(data[name])]));
}

/** A row's data from its fields. Another recorded field keeps its recorded value until it is edited, and a cleared one is removed. */
export function dataOf(fields: readonly RowField[], values: Record<string, string>, recorded: Record<string, unknown>): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const f of fields) {
    const value = fieldValue(f, values[f.name] ?? "");
    if (value !== undefined) data[f.name] = value;
  }
  for (const name of otherFields(fields, recorded)) {
    const raw = values[name] ?? shownValue(recorded[name]);
    if (raw === shownValue(recorded[name])) data[name] = recorded[name];
    else if (raw.trim()) data[name] = raw.trim();
  }
  return data;
}

/** An instructor row's record choice: an existing faculty record's id, `create`, or undecided. */
export type InstructorChoice = string;

export function instructorChoiceOf(data: Record<string, unknown>): InstructorChoice {
  if (typeof data.faculty_profile_id === "string") return data.faculty_profile_id;
  return typeof data.display_name === "string" ? "create" : "";
}

/** The name an instructor row's source wrote, for its accountless record. */
export function instructorName(row: ImportRowInput): string {
  return String(row.data?.display_name ?? row.source_ref?.name ?? row.import_key);
}

export function instructorData(choice: InstructorChoice, name: string): Record<string, unknown> {
  if (!choice) return {};
  if (choice === "create") return { display_name: name.trim(), account_link_state: "not_linked", portal_access: "none" };
  return { faculty_profile_id: choice };
}

export const composite = (kind: string, key: string) => `${kind}:${key}`;

const keyList = (value: unknown): string[] => Array.isArray(value) ? value.filter((v): v is string => typeof v === "string")
  : typeof value === "string" ? value.split(";").map((v) => v.trim()).filter(Boolean) : [];
const key = (value: unknown) => typeof value === "string" && value.trim() ? [value.trim()] : [];

/** The rows a row refers to, by composite key, as the server's retry scope walks them. */
export function parentsOf(batchKind: ImportKind, kind: EntityKind, data: Record<string, unknown>): string[] {
  if (kind === "course" && batchKind === "historical") return [...key(data.term_key).map((k) => composite("term", k)),
    ...[...key(data.primary_faculty_key), ...keyList(data.co_instructor_faculty_keys)].map((k) => composite("faculty", k))];
  if (kind === "enrollment") return batchKind === "registrar_roster" ? key(data.student_key).map((k) => composite("student", k))
    : [...key(data.student_key).map((k) => composite("student", k)), ...key(data.course_key).map((k) => composite("course", k))];
  if (kind === "outcome") return key(data.enrollment_key).map((k) => composite("enrollment", k));
  return [];
}

/** The seeds and every row they depend on, recursively, among the rows `known` names. Seeds first, then parents in walk order. */
export function withParents(seeds: string[], parents: (id: string) => string[], known: (id: string) => boolean): string[] {
  const scope = [...seeds];
  const seen = new Set(seeds);
  for (let i = 0; i < scope.length; i += 1) {
    for (const parent of parents(scope[i])) {
      if (seen.has(parent) || !known(parent)) continue;
      seen.add(parent); scope.push(parent);
    }
  }
  return scope;
}

/** Refused receipts that recorded their row, so they can be fixed here. */
/** Parents before the rows that depend on them, so the row whose refusal caused the others reads first. */
const PARENTS_FIRST: readonly ImportReceipt["entity_kind"][] = ["term", "faculty", "course", "student", "enrollment", "outcome"];

export function fixableReceipts(receipts: ImportReceipt[]): ImportReceipt[] {
  return receipts.filter((r) => r.action === "rejected" && receiptRow(r) !== null)
    .map((r, at) => ({ r, at }))
    .sort((x, y) => PARENTS_FIRST.indexOf(x.r.entity_kind) - PARENTS_FIRST.indexOf(y.r.entity_kind) || x.at - y.at)
    .map(({ r }) => r);
}

/** Refused receipts from before row data was kept: fixed in the file instead. */
export function unrecordedRefusals(receipts: ImportReceipt[]): ImportReceipt[] {
  return receipts.filter((r) => r.action === "rejected" && receiptRow(r) === null);
}

/** The receipt's source evidence, without the service's own keys. */
export function sourceEvidence(ref: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return Object.fromEntries(Object.entries(ref ?? {}).filter(([k]) => !(RECEIPT_SERVICE_KEYS as readonly string[]).includes(k)));
}

/** One refused row as the registrar fixed it: its data, or left out of the resend. */
export interface RowFix { leaveOut: boolean; data: Record<string, unknown> }

export interface CorrectionRows { name: string; rows: ImportRowInput[]; choices: Record<string, LearnerChoice>; parents: number }

const resolvedHere = (r: ImportReceipt) => (r.action === "created" || r.action === "matched" || r.action === "corrected") && r.resolved_id !== null;

/**
 * The correction source: every fixed row that is not left out, then each row
 * it depends on, rebuilt from this run's receipts. A parent this run created or
 * matched is sent to match by key: an instructor names its record, a learner
 * without a source ID links to its record. Any other parent is sent as recorded,
 * with its recorded learner choice. A left-out refused row is never sent, even
 * as a parent.
 */
export function correctionRows(sourceName: string, batchKind: ImportKind, receipts: ImportReceipt[], fixes: Record<string, RowFix>): CorrectionRows {
  const byId = new Map<string, ImportReceipt>();
  for (const r of receipts) if (receiptRow(r) && !byId.has(composite(r.entity_kind, r.import_key))) byId.set(composite(r.entity_kind, r.import_key), r);
  const leftOut = new Set(fixableReceipts(receipts).filter((r) => fixes[r.id]?.leaveOut).map((r) => composite(r.entity_kind, r.import_key)));
  const rowOf = (r: ImportReceipt): ImportRowInput => {
    const row = receiptRow(r)!;
    const fix = fixes[r.id];
    if (r.action === "rejected" && fix) return { ...row, data: fix.data };
    if (resolvedHere(r) && r.entity_kind === "faculty") return { ...row, data: { faculty_profile_id: r.resolved_id } };
    return row;
  };
  const seeds = fixableReceipts(receipts).filter((r) => !fixes[r.id]?.leaveOut).map((r) => composite(r.entity_kind, r.import_key));
  const scope = withParents(seeds, (id) => {
    const r = byId.get(id)!;
    return parentsOf(batchKind, r.entity_kind, rowOf(r).data ?? {});
  }, (id) => byId.has(id) && !leftOut.has(id));
  const rows = scope.map((id) => rowOf(byId.get(id)!));
  const choices: Record<string, LearnerChoice> = {};
  for (const id of scope) {
    const r = byId.get(id)!;
    const row = rowOf(r);
    if (r.entity_kind !== "student" || row.data?.student_id) continue;
    const choice = r.action !== "rejected" && resolvedHere(r) ? { action: "link" as const, student_id: r.resolved_id! } : receiptLearnerChoice(r);
    if (choice) choices[r.import_key] = choice;
  }
  return { name: `${sourceName} · corrections`, rows, choices, parents: scope.length - seeds.length };
}

/** JSON with every object's keys in order, so the same rows always read the same. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().filter((k) => (value as Record<string, unknown>)[k] !== undefined)
      .map((k) => `${JSON.stringify(k)}:${canonicalJson((value as Record<string, unknown>)[k])}`).join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

/**
 * The correction source's digest. A correction has no file, so the digest is
 * of the rows themselves: lowercase hex SHA-256 of their canonical JSON. The
 * same fixes always give the same digest, and any change gives another.
 */
export async function rowsDigest(rows: ImportRowInput[]): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonicalJson(rows)));
  return Array.from(new Uint8Array(digest), (v) => v.toString(16).padStart(2, "0")).join("");
}
